import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBoddy, DialogFooter, DialogHeader } from './dialog';
import {
  Dropdown,
  DropdownTriggerButton,
  DropdownContent,
  DropDownSelectToClose,
} from './Dropdown';

export interface EditableDropdownItem {
  id: string | number;
  label: string;
}

export interface EditableDropdownProps {
  items: EditableDropdownItem[];
  selectedId: string | number | null;
  onSelect: (id: string | number) => void;
  onAdd?: (name: string) => void;
  onRename?: (id: string | number, newName: string) => void;
  onDelete?: (id: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function EditableDropdown({
  items,
  selectedId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  placeholder,
  disabled = false,
}: EditableDropdownProps) {
  const { t } = useTranslation();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const selectedItem = items.find((item) => item.id === selectedId);

  return (
    <div className="flex items-center gap-1">
      <Dropdown>
        <DropdownTriggerButton disabled={disabled}>
          <span className="truncate max-w-40">
            {selectedItem?.label || placeholder || t('editableDropdown.select')}
          </span>
          <svg
            className="shrink-0 size-3.5"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </DropdownTriggerButton>

        <DropdownContent>
          <div className="p-1">
            {items.map((item) => (
              <DropDownSelectToClose key={item.id} doSelect={() => onSelect(item.id)}>
                <button
                  type="button"
                  className={`w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-surface-darker ${
                    selectedId === item.id ? 'text-primary' : 'text-onsurface'
                  }`}
                >
                  {item.label}
                </button>
              </DropDownSelectToClose>
            ))}
            {items.length === 0 && (
              <div className="py-2 px-3 text-sm text-muted-foreground">
                {t('editableDropdown.noItems')}
              </div>
            )}
          </div>
        </DropdownContent>
      </Dropdown>

      {(onAdd || onRename || onDelete) && (
        <button
          type="button"
          onClick={() => setIsEditDialogOpen(true)}
          disabled={disabled}
          className="p-1.5 rounded-md border border-border bg-surface text-muted-foreground hover:bg-surface-darker hover:text-onsurface disabled:opacity-50 disabled:pointer-events-none"
          aria-label={t('editableDropdown.edit')}
        >
          <svg
            className="size-3.5"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            <path d="m15 5 4 4" />
          </svg>
        </button>
      )}

      {isEditDialogOpen && (
        <EditDialog
          items={items}
          onAdd={onAdd}
          onRename={onRename}
          onDelete={onDelete}
          onClose={() => setIsEditDialogOpen(false)}
        />
      )}
    </div>
  );
}

interface EditDialogProps {
  items: EditableDropdownItem[];
  onAdd?: (name: string) => void;
  onRename?: (id: string | number, newName: string) => void;
  onDelete?: (id: string | number) => void;
  onClose: () => void;
}

function EditDialog({ items, onAdd, onRename, onDelete, onClose }: EditDialogProps) {
  const { t } = useTranslation();
  const [localItems, setLocalItems] = useState<EditableDropdownItem[]>(items);
  const [newItemName, setNewItemName] = useState('');
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleAdd = () => {
    const trimmed = newItemName.trim();
    if (!trimmed) return;
    onAdd?.(trimmed);
    setLocalItems([...localItems, { id: `new-${Date.now()}`, label: trimmed }]);
    setNewItemName('');
  };

  const handleRename = (id: string | number) => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    onRename?.(id, trimmed);
    setLocalItems(localItems.map((item) => (item.id === id ? { ...item, label: trimmed } : item)));
    setEditingId(null);
    setEditingName('');
  };

  const handleDelete = (id: string | number) => {
    onDelete?.(id);
    setLocalItems(localItems.filter((item) => item.id !== id));
  };

  return (
    <Dialog isOpen={true} onClose={onClose}>
      <DialogHeader>{t('editableDropdown.editTitle')}</DialogHeader>
      <DialogBoddy className="pt-4">
        <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
          {localItems.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              {editingId === item.id ? (
                <>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename(item.id);
                      if (e.key === 'Escape') {
                        setEditingId(null);
                        setEditingName('');
                      }
                    }}
                    className="flex-1 py-1 px-2 text-sm bg-surface-darker border border-border rounded-md text-onsurface focus:border-primary focus:ring-primary focus:outline-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => handleRename(item.id)}
                    className="p-1 text-primary hover:text-primary-darker"
                  >
                    <svg className="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingId(null); setEditingName(''); }}
                    className="p-1 text-muted-foreground hover:text-onsurface"
                  >
                    <svg className="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm text-onsurface py-1 px-2">{item.label}</span>
                  {onRename && (
                    <button
                      type="button"
                      onClick={() => { setEditingId(item.id); setEditingName(item.label); }}
                      className="p-1 text-muted-foreground hover:text-onsurface"
                      aria-label={t('editableDropdown.rename')}
                    >
                      <svg className="size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      className="p-1 text-muted-foreground hover:text-error"
                      aria-label={t('editableDropdown.delete')}
                    >
                      <svg className="size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {onAdd && (
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              placeholder={t('editableDropdown.addPlaceholder')}
              className="flex-1 py-1.5 px-2 text-sm bg-surface-darker border border-border rounded-md text-onsurface placeholder:text-muted-foreground focus:border-primary focus:ring-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={!newItemName.trim()}
              className="py-1.5 px-3 text-sm font-medium rounded-md bg-primary text-onprimary hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none"
            >
              {t('editableDropdown.add')}
            </button>
          </div>
        )}

      </DialogBoddy>
      <DialogFooter>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="py-2 px-4 text-sm font-medium rounded-md border border-border bg-surface text-onsurface hover:bg-surface-darker"
          >
            {t('editableDropdown.close')}
          </button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}
